import type { GraphQLResult } from 'aws-amplify/api'
import {
  type QueryClient,
  type QueryKey,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'

import { getAmplifyClient } from '@/services/amplifyClient'
import {
  createSavedSearchMutation,
  updateSavedSearchMutation,
} from '@/services/graphql/mutations'
import type { SavedSearchItem } from '@/services/adapters/savedSearch.adapter'
import { trackUsageEvent } from '@/utils/posthog.utils'
import { buildSavedSearchUpdateInput } from './savedSearchAuthority.shared'

export type DimensionRangeInput = {
  key?: string
  min?: number
  max?: number
}

export type SavedSearchInput = {
  id: string
  status?: string
  byUserID?: string
  organizationID?: string
  organizationMasterID?: string
  projectID?: string | null
  name?: string
  source?: string
  address?: string
  description?: string
  conditions?: string[]
  provenances?: string[]
  materials?: string[]
  dimensionRange?: DimensionRangeInput[]
  quantity?: { number: number; unit: string }
  location?: { lat: number; lon: number }
  startDate?: string | null
  endDate?: string | null
}

const MANAGE_SOURCE = 'manage'

type SavedSearchMutationResult =
  | { id: string; createdAt?: string; updatedAt?: string }
  | null
  | undefined

type SavedSearchesQueryScope = {
  userID?: string
  organizationID?: string
  organizationMasterID?: string
  filters?: { projectID?: { eq?: string } }
  isMaster?: boolean
}

const isSavedSearchesQueryKey = (
  queryKey: QueryKey,
): queryKey is ['savedSearches', SavedSearchesQueryScope] =>
  Array.isArray(queryKey) &&
  queryKey[0] === 'savedSearches' &&
  typeof queryKey[1] === 'object' &&
  queryKey[1] !== null

const mergeSavedSearchInput = (
  existing: SavedSearchItem | undefined,
  input: SavedSearchInput,
  result?: SavedSearchMutationResult,
): SavedSearchItem => {
  const timestamp = result?.updatedAt ?? result?.createdAt
  return {
    ...existing,
    id: input.id,
    byUserID: input.byUserID ?? existing?.byUserID,
    projectID:
      input.projectID !== undefined ? input.projectID : existing?.projectID,
    organizationID: input.organizationID ?? existing?.organizationID,
    organizationMasterID:
      input.organizationMasterID ?? existing?.organizationMasterID,
    location: input.location ?? existing?.location,
    name: input.name ?? existing?.name,
    source: input.source ?? existing?.source,
    status: input.status ?? existing?.status ?? 'enabled',
    description: input.description ?? existing?.description,
    conditions: input.conditions ?? existing?.conditions,
    provenances: input.provenances ?? existing?.provenances,
    dimensionRange: input.dimensionRange ?? existing?.dimensionRange,
    quantity: input.quantity ?? existing?.quantity,
    materials: input.materials ?? existing?.materials,
    address: input.address ?? existing?.address,
    startDate:
      input.startDate !== undefined ? input.startDate : existing?.startDate,
    endDate: input.endDate !== undefined ? input.endDate : existing?.endDate,
    createdAt: existing?.createdAt ?? result?.createdAt,
    updatedAt: result?.updatedAt ?? timestamp ?? existing?.updatedAt,
  }
}

const savedSearchMatchesScope = (
  item: SavedSearchItem,
  scope: SavedSearchesQueryScope,
) => {
  if (item.status === 'deleted') return false

  const projectID = scope.filters?.projectID?.eq
  if (projectID) return item.projectID === projectID

  if (scope.isMaster) {
    const masterScopeID = scope.organizationMasterID ?? scope.organizationID
    return Boolean(
      (scope.organizationID && item.organizationID === scope.organizationID) ||
      (masterScopeID && item.organizationMasterID === masterScopeID),
    )
  }

  if (scope.organizationID) return item.organizationID === scope.organizationID
  if (scope.userID) return item.byUserID === scope.userID

  return false
}

export const updateSavedSearchListCaches = ({
  queryClient,
  input,
  result,
  upsertType = 'update',
}: {
  queryClient: QueryClient
  input: SavedSearchInput
  result?: SavedSearchMutationResult
  upsertType?: 'create' | 'update'
}) => {
  const queries = queryClient
    .getQueryCache()
    .findAll({ queryKey: ['savedSearches'] })
  const cachedItem = queries
    .map(({ queryKey }) =>
      queryClient
        .getQueryData<SavedSearchItem[]>(queryKey)
        ?.find((item) => item.id === input.id),
    )
    .find((item) => item !== undefined)

  if (!cachedItem && upsertType === 'update') {
    void queryClient.invalidateQueries({
      queryKey: ['savedSearches'],
      refetchType: 'active',
    })
    return
  }

  queries.forEach(({ queryKey }) => {
    if (!isSavedSearchesQueryKey(queryKey)) return
    const [, scope] = queryKey

    queryClient.setQueryData<SavedSearchItem[]>(queryKey, (current = []) => {
      const existing = current.find((item) => item.id === input.id)
      const next = mergeSavedSearchInput(existing ?? cachedItem, input, result)
      const shouldKeep = savedSearchMatchesScope(next, scope)

      if (!existing && !shouldKeep) return current
      if (!shouldKeep) return current.filter((item) => item.id !== input.id)

      if (existing) {
        return current.map((item) => (item.id === input.id ? next : item))
      }

      return [next, ...current]
    })
  })
}

const invalidateSavedSearchQueries = (
  queryClient: QueryClient,
  id?: string,
) => {
  void queryClient.invalidateQueries({
    queryKey: ['savedSearches'],
    refetchType: 'inactive',
  })
  void queryClient.invalidateQueries({
    queryKey: ['projectAlertsTotal'],
    refetchType: 'all',
  })
  if (id) {
    void queryClient.invalidateQueries({
      queryKey: ['savedSearch', id],
      refetchType: 'inactive',
    })
  }
}

const createSavedSearch = async (input: SavedSearchInput) => {
  const client = getAmplifyClient()
  const { data } = (await client.graphql({
    query: createSavedSearchMutation,
    variables: { input: { ...input, source: MANAGE_SOURCE } },
  })) as GraphQLResult<{ createSavedSearch: { id: string; createdAt: string } }>
  if (!data?.createSavedSearch?.id) {
    throw new Error('Create saved search returned no result')
  }
  return data.createSavedSearch
}

const updateSavedSearch = async (input: SavedSearchInput) => {
  const client = getAmplifyClient()
  const { data } = (await client.graphql({
    query: updateSavedSearchMutation,
    variables: { input: buildSavedSearchUpdateInput(input) },
  })) as GraphQLResult<{ updateSavedSearch: { id: string; updatedAt: string } }>
  if (!data?.updateSavedSearch?.id) {
    throw new Error('Update saved search returned no result')
  }
  return data.updateSavedSearch
}

export const useUpsertSavedSearch = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['upsertSavedSearch'],
    mutationFn: async ({
      upsertType,
      input,
    }: {
      upsertType: 'create' | 'update'
      input: SavedSearchInput
    }) => {
      if (upsertType === 'create') {
        return createSavedSearch(input)
      }
      return updateSavedSearch(input)
    },
    onSuccess: (data, variables) => {
      updateSavedSearchListCaches({
        queryClient,
        upsertType: variables.upsertType,
        input:
          variables.upsertType === 'update'
            ? buildSavedSearchUpdateInput(variables.input)
            : { ...variables.input, source: MANAGE_SOURCE },
        result: data,
      })
      invalidateSavedSearchQueries(queryClient, variables.input.id)
      trackUsageEvent('feature_used', {
        feature: 'alerts',
        action:
          variables.upsertType === 'create'
            ? 'create_saved_search'
            : 'update_saved_search',
        surface: 'alerts',
        result: 'success',
        ...(typeof variables.input.status === 'string'
          ? {
              status_action: variables.input.status,
              setting_enabled: variables.input.status !== 'disabled',
            }
          : {}),
      })
    },
  })
}

export const useUpdateSavedSearchStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['updateSavedSearchStatus'],
    mutationFn: async ({
      id,
      status,
    }: {
      id: string
      status: 'enabled' | 'disabled'
    }) => {
      return updateSavedSearch({ id, status })
    },
    onSuccess: (data, variables) => {
      updateSavedSearchListCaches({
        queryClient,
        input: { id: variables.id, status: variables.status },
        result: data,
      })
      invalidateSavedSearchQueries(queryClient, variables.id)
      trackUsageEvent('feature_used', {
        feature: 'alerts',
        action: 'update_saved_search_status',
        surface: 'alerts',
        result: 'success',
        status_action: variables.status,
        setting_enabled: variables.status === 'enabled',
      })
    },
  })
}

export const useDeleteSavedSearch = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['deleteSavedSearch'],
    mutationFn: async (id: string) => {
      return updateSavedSearch({ id, status: 'deleted' })
    },
    onSuccess: (data, id) => {
      updateSavedSearchListCaches({
        queryClient,
        input: { id, status: 'deleted' },
        result: data,
      })
      invalidateSavedSearchQueries(queryClient, id)
      trackUsageEvent('feature_used', {
        feature: 'alerts',
        action: 'delete_saved_search',
        surface: 'alerts',
        result: 'success',
      })
    },
  })
}
